import { type ReactNode } from 'react';
import { type PanelSpan } from '@/components/shared';
import { formatPrimitiveSetting, formatSettingLabel } from '../format';
import type { BackofficeOrganizationDetail } from '../types';
import { OrganizationDetailPanel } from './OrganizationDetailPanelGrid';

export interface OrganizationSettingsPanelProps {
  organization: BackofficeOrganizationDetail;
  panelId?: string | undefined;
  currentSpan?: PanelSpan | undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function SettingsTree({ value, depth = 0 }: { value: unknown; depth?: number }) {
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span className="text-neutral-500">No values</span>;
    }

    return (
      <ul className="space-y-2">
        {value.map((item, index) => (
          <li key={index} className="border-l border-neutral-200 pl-3">
            <SettingsTree value={item} depth={depth + 1} />
          </li>
        ))}
      </ul>
    );
  }

  if (!isRecord(value)) {
    return <span>{formatPrimitiveSetting(value)}</span>;
  }

  const entries = Object.entries(value);
  if (entries.length === 0) {
    return <span className="text-neutral-500">No settings</span>;
  }

  return (
    <dl className={depth === 0 ? 'space-y-3' : 'space-y-2'}>
      {entries.map(([key, item]) => {
        const nested = isRecord(item) || Array.isArray(item);
        const content: ReactNode = nested ? (
          <div className="mt-2 border-l border-neutral-200 pl-3">
            <SettingsTree value={item} depth={depth + 1} />
          </div>
        ) : (
          <span className="text-neutral-700">{formatPrimitiveSetting(item)}</span>
        );

        return (
          <div key={key} className="border-b border-neutral-100 pb-3 last:border-b-0">
            <dt className="font-semibold text-neutral-900">{formatSettingLabel(key)}</dt>
            <dd className="mt-1 min-w-0 text-neutral-700">{content}</dd>
          </div>
        );
      })}
    </dl>
  );
}

export function OrganizationSettingsPanel({
  organization,
  panelId,
  currentSpan,
}: OrganizationSettingsPanelProps) {
  return (
    <OrganizationDetailPanel
      title="Settings"
      span="2x2"
      panelId={panelId}
      currentSpan={currentSpan}
    >
      <div className="text-body">
        <SettingsTree value={organization.settings} />
      </div>
    </OrganizationDetailPanel>
  );
}
